import type { StatusDefinition } from "@kontextwerk/web-sdk/core/display/status"
import { mdiCalendarClockOutline } from "@mdi/js"
import { formatPropertyUnitLabel } from "../../../../../../../_shared/_logic/unit-labels"

import type {
  KehrblattCaseCommunicationAppointment,
  KehrblattCaseCommunicationExtensionData,
} from "../../../../../../../api/kehrblatt/case-communications"
import type { ScheduleEventStatus } from "../../../../../../../_shared/_logic/enums"
import { buildScheduleEventCommunicationTagPresentation } from "../../../../../../../_shared/_logic/communication-presentation"
import {
  buildRichTooltip,
  tooltipField,
  tooltipText,
} from "../../../../../../../_shared/_logic/rich-tooltips"
import { internalOrderTypeLabel } from "../../../../../../../_shared/_logic/order-type-labels"

export type CommunicationWorkPresentation = {
  status: StatusDefinition<string>
  subLabel: string
  orderNumber: string
  tooltipHtml: string
  ariaLabel: string
  searchText: string
  sortValue: string
}

const appointmentRolePriority: Record<string, number> = {
  current: 0,
  new: 1,
  canceled: 2,
  old: 3,
}

const normalized = (value?: string | null) => String(value ?? "").trim()
const DISPLAY_TIME_ZONE = "Europe/Berlin"

const compactDateFormatter = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  timeZone: DISPLAY_TIME_ZONE,
})

const fullDateFormatter = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: DISPLAY_TIME_ZONE,
})

const timeFormatter = new Intl.DateTimeFormat("de-DE", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
  timeZone: DISPLAY_TIME_ZONE,
})

export const selectCommunicationWorkAppointment = (
  appointments: KehrblattCaseCommunicationAppointment[] = [],
): KehrblattCaseCommunicationAppointment | null =>
  appointments
    .map((appointment, index) => ({ appointment, index }))
    .sort((left, right) => {
      const leftPriority = appointmentRolePriority[left.appointment.role] ?? Number.MAX_SAFE_INTEGER
      const rightPriority =
        appointmentRolePriority[right.appointment.role] ?? Number.MAX_SAFE_INTEGER

      return leftPriority - rightPriority || left.index - right.index
    })[0]?.appointment ?? null

const durationMinutes = (
  appointment: KehrblattCaseCommunicationAppointment | null,
  fallback?: number | null,
) => {
  if (appointment?.start_at && appointment.end_at) {
    const start = Date.parse(appointment.start_at)
    const end = Date.parse(appointment.end_at)

    if (Number.isFinite(start) && Number.isFinite(end) && end >= start) {
      return Math.round((end - start) / 60_000)
    }
  }

  return typeof fallback === "number" && Number.isFinite(fallback) ? fallback : null
}

export const formatCommunicationWorkAppointment = (
  appointment: KehrblattCaseCommunicationAppointment | null,
  fallbackDuration?: number | null,
) => {
  const start = appointment?.start_at ? new Date(appointment.start_at) : null

  if (!start || Number.isNaN(start.getTime())) return "Kein Terminbezug"

  const date = fullDateFormatter.format(start)
  const startTime = timeFormatter.format(start)
  const end = appointment?.end_at ? new Date(appointment.end_at) : null
  const hasValidEnd = Boolean(
    end && !Number.isNaN(end.getTime()) && end.getTime() >= start.getTime(),
  )
  const time =
    hasValidEnd && end ? `${startTime}–${timeFormatter.format(end)} Uhr` : `${startTime} Uhr`
  const duration = durationMinutes(appointment, fallbackDuration)

  return `${date} ${time}${duration === null ? "" : ` (${duration} Min.)`}`
}

const appointmentStatusPresentation = (
  appointment: KehrblattCaseCommunicationAppointment | null,
  label: string,
): StatusDefinition<string> => {
  if (!appointment) {
    return {
      value: "NO_APPOINTMENT_REF",
      label,
      color: "slate",
      icon: mdiCalendarClockOutline,
      description: "Kein konkreter Termin verknüpft.",
    }
  }

  const presentation = buildScheduleEventCommunicationTagPresentation({
    status: (normalized(appointment.status) || "RESERVED") as ScheduleEventStatus,
    presentation: appointment.communication_presentation,
  })

  return {
    ...presentation.definition,
    label,
  }
}

const propertySegments = (extension: KehrblattCaseCommunicationExtensionData | null) => {
  const property = extension?.property
  const address = [normalized(property?.street), normalized(property?.house_number)]
    .filter(Boolean)
    .join(" ")
  const unit =
    normalized(extension?.work_order?.property_unit_label) || normalized(property?.unit_label)

  return { address: address || null, unit: unit ? formatPropertyUnitLabel(unit) : null }
}

const formatTooltipAppointment = (appointment?: KehrblattCaseCommunicationAppointment | null) =>
  appointment ? formatCommunicationWorkAppointment(appointment) : null

const compactAppointmentDate = (appointment: KehrblattCaseCommunicationAppointment | null) => {
  const date = appointment?.start_at ? new Date(appointment.start_at) : null

  if (!date || Number.isNaN(date.getTime())) return null

  return compactDateFormatter.format(date)
}

const appointmentTime = (appointment: KehrblattCaseCommunicationAppointment | null) => {
  const date = appointment?.start_at ? new Date(appointment.start_at) : null

  if (!date || Number.isNaN(date.getTime())) return null

  return `${timeFormatter.format(date)} Uhr`
}

const orderTypeLabel = (extension: KehrblattCaseCommunicationExtensionData | null) => {
  const label = internalOrderTypeLabel(extension?.work_order)

  if (!label) return "Auftrag"

  return label
    .split(/\s*\/\s*/)
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" / ")
}

const dueDateLabel = (value?: string | null) => {
  const date = value ? new Date(value) : null

  if (!date || Number.isNaN(date.getTime())) return null

  return fullDateFormatter.format(date)
}

export const buildCommunicationWorkPresentation = (
  extension: KehrblattCaseCommunicationExtensionData | null,
): CommunicationWorkPresentation => {
  const appointments = extension?.appointments ?? []
  const appointment = selectCommunicationWorkAppointment(appointments)
  const previousAppointment = appointments.find((candidate) => candidate.role === "old") ?? null
  const fullAppointmentLabel = formatCommunicationWorkAppointment(
    appointment,
    extension?.work_order?.estimated_duration_min ?? null,
  )
  const typeLabel = orderTypeLabel(extension)
  const compactDate = compactAppointmentDate(appointment)
  const label = compactDate ? `${typeLabel} am ${compactDate}` : `${typeLabel} · Kein Termin`
  const { address, unit } = propertySegments(extension)
  const time = appointmentTime(appointment)
  const communication = appointment
    ? buildScheduleEventCommunicationTagPresentation({
        status: appointment.status ?? "RESERVED",
        presentation: appointment.communication_presentation,
      })
    : null
  const subLabel =
    [address, unit, time, communication?.label].filter(Boolean).join(" · ") || "Kein Ortsbezug"
  const orderNumber = normalized(extension?.work_order?.order_number) || "–"
  const property = extension?.property
  const cityLine = [normalized(property?.zip), normalized(property?.city)].filter(Boolean).join(" ")
  const location =
    [address, cityLine, normalized(property?.country)].filter(Boolean).join(", ") || null
  const appointmentStatus = communication?.label ?? null
  const employee = normalized(appointment?.employee_name) || null
  const duration = durationMinutes(
    appointment,
    extension?.work_order?.estimated_duration_min ?? null,
  )
  const dueDate = dueDateLabel(extension?.work_order?.due_date)
  const searchText = [
    typeLabel,
    compactDate,
    fullAppointmentLabel,
    address,
    unit,
    time,
    employee,
    appointmentStatus,
    location,
    orderNumber,
  ]
    .filter(Boolean)
    .join(" ")

  return {
    status: appointmentStatusPresentation(appointment, label),
    subLabel,
    orderNumber,
    ariaLabel: `${label}, ${subLabel}, Auftrag ${orderNumber}`,
    searchText,
    sortValue: appointment?.start_at || typeLabel,
    tooltipHtml:
      (communication?.tooltip ?? "") +
      buildRichTooltip([
        appointment?.end_at && Number.isFinite(Date.parse(appointment.end_at))
          ? tooltipField(
              "Geplantes Ende",
              `${timeFormatter.format(new Date(appointment.end_at))} Uhr${duration === null ? "" : ` (${duration} Min.)`}`,
            )
          : duration === null
            ? null
            : tooltipField("Dauer", `${duration} Minuten`),
        tooltipField("Mitarbeiter", employee),
        appointment && ["current", "new"].includes(appointment.role) && previousAppointment
          ? tooltipField("Bisheriger Termin", formatTooltipAppointment(previousAppointment))
          : null,
        tooltipField("Ort", cityLine),
        property?.address_extra ? tooltipField("Adresszusatz", property.address_extra) : null,
        tooltipField("Fällig", dueDate),
        appointment ? null : tooltipText("Diese Nachricht gehört zu keinem bestimmten Termin."),
      ]),
  }
}
